import React, { useEffect, useRef, useState } from "react";
import API from "../../services/api";

const endpoint = "/opcua/client-certificate/";
const list = (value) => value.split(",").map((part) => part.trim()).filter(Boolean);
const errorMessage = (error) => {
    const data = error?.response?.data;
    if (data?.detail) return String(data.detail);
    return data ? Object.entries(data).map(([field, errors]) => `${field}: ${[].concat(errors).join(" ")}`).join(" ") : "Client certificate request failed.";
};

// Mounted only for a superadmin. The API independently enforces that boundary.
export default function ClientCertificateManager({ onIdentityChange }) {
    const [identity, setIdentity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [available, setAvailable] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [draft, setDraft] = useState({ common_name: "Aqueris OPC UA client", organization: "Aqueris", dns_names: "", ip_addresses: "", validity_days: 365 });
    const inFlight = useRef(false);

    useEffect(() => {
        let cancelled = false;
        API.get(endpoint).then(({ data }) => {
            if (!cancelled) {
                setIdentity(data.identity || null);
                onIdentityChange(data.identity || null);
                setAvailable(true);
            }
        }).catch((failure) => { if (!cancelled) setError(errorMessage(failure)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [onIdentityChange]);

    const create = async (event) => {
        event.preventDefault();
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const { data } = await API.post(endpoint, { ...draft, dns_names: list(draft.dns_names), ip_addresses: list(draft.ip_addresses), validity_days: Number(draft.validity_days) });
            setIdentity(data.identity);
            onIdentityChange(data.identity);
            setNotice(data.created ? "Client certificate created. No server settings were changed." : "The existing client certificate and private key were preserved.");
        } catch (failure) {
            setError(errorMessage(failure));
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    const download = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        setError("");
        try {
            const { data } = await API.get(`${endpoint}public/`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = "aqueris-opcua-client.der";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (failure) {
            setError("Public certificate download failed. Refresh the page and check superadmin access.");
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    return <section className="server-page__card" aria-labelledby="client-certificate-heading">
        <div className="server-page__card-head">
            <h2 id="client-certificate-heading">Shared client certificate — superadmin only</h2>
            <p>One application identity for this installation. The private key stays on this server and cannot be downloaded. Import only the public DER certificate into each PLC's trusted clients.</p>
        </div>
        {loading ? <p role="status">Loading client certificate…</p> : error && !identity ? <p className="server-page__banner is-error" role="alert">{error}</p> : null}
        {!loading && identity ? <>
            <dl>
                <dt>Client name</dt><dd>{identity.common_name}</dd>
                <dt>Application URI</dt><dd>{identity.application_uri}</dd>
                <dt>Valid until</dt><dd>{new Date(identity.valid_until).toLocaleString()}</dd>
                <dt>SHA-256 fingerprint</dt><dd style={{ overflowWrap: "anywhere" }}>{identity.fingerprint_sha256}</dd>
            </dl>
            <button type="button" className="server-page__button" disabled={busy} onClick={download}>Download public client certificate (.der)</button>
            <p>Existing keys are never replaced automatically. Keep a protected server-side backup of the certificate and private key. In each server's certificate settings, choose “Use shared client certificate”, then save when ready.</p>
        </> : null}
        {!loading && !identity && available ? <form onSubmit={create}>
            <div className="server-page__grid">
                {[
                    ["common_name", "Client application name", "text", 64],
                    ["organization", "Operating organization", "text", 128],
                    ["dns_names", "Client host DNS names (comma-separated, optional)", "text", 4096],
                    ["ip_addresses", "Client host IP addresses (comma-separated, optional)", "text", 1024],
                    ["validity_days", "Certificate validity (days)", "number"],
                ].map(([name, label, type, maxLength]) => <div className="server-page__field" key={name}>
                    <label htmlFor={`client-identity-${name}`}>{label}</label>
                    <input id={`client-identity-${name}`} type={type} maxLength={maxLength} min={type === "number" ? 1 : undefined} max={type === "number" ? 730 : undefined}
                        value={draft[name]} required={["common_name", "organization", "validity_days"].includes(name)} disabled={busy}
                        onChange={(event) => setDraft((current) => ({ ...current, [name]: event.target.value }))} />
                </div>)}
            </div>
            <p>Use the collector host's DNS/IP identity, not the PLC address. Blank DNS uses this server's hostname. A unique client Application URI is generated once. RSA 2048 / SHA-256; self-signed, with explicit PLC trust required.</p>
            <button type="submit" className="server-page__button server-page__button--primary" disabled={busy}>{busy ? "Creating…" : "Create shared client certificate"}</button>
        </form> : null}
        {error && identity ? <p className="server-page__banner is-error" role="alert">{error}</p> : null}
        {notice ? <p role="status">{notice}</p> : null}
    </section>;
}
